'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Image from 'next/image';
import { Terminal, Send } from 'lucide-react';

interface DemoCommand {
  command: string;
  description: string;
  response: string;
}

const demoCommands: DemoCommand[] = [
  {
    command: ',serverinfo',
    description: 'View server information',
    response: '**Server Information**\n\n**Name:** My Awesome Server\n**Owner:** @Admin\n**Members:** 1,234\n**Created:** January 15, 2023\n**Boost Level:** Level 2 🚀',
  },
  {
    command: ',antinuke setup',
    description: 'Enable antinuke protection',
    response: '✅ **Antinuke System Activated**\n\nAntinuke logs channel created: #antinuke-logs\n\nNow monitoring:\n• Member joins/leaves\n• Bans and kicks\n• Role changes\n• Channel modifications\n• Voice moves\n\nYour server is now protected!',
  },
  {
    command: ',tictactoe @friend',
    description: 'Start a Tic-Tac-Toe game',
    response: '🎮 **Tic-Tac-Toe Game Started!**\n\n@You vs @friend\n\n```\n ⬜ | ⬜ | ⬜ \n---|---|---\n ⬜ | ⬜ | ⬜ \n---|---|---\n ⬜ | ⬜ | ⬜ \n```\n\n@You\'s turn! Choose a position (1-9)',
  },
  {
    command: ',warn @user spam',
    description: 'Warn a user',
    response: '⚠️ **User Warned**\n\n**User:** @user\n**Reason:** spam\n**Warned by:** @You\n\nThis is warning #1 for @user\n\nA log has been created in #mod-logs',
  },
];

export function LiveDemo() {
  const [selectedCommand, setSelectedCommand] = useState<DemoCommand>(demoCommands[0]);
  const [showResponse, setShowResponse] = useState(false);

  const handleCommandClick = (cmd: DemoCommand) => {
    setSelectedCommand(cmd);
    setShowResponse(false);
    setTimeout(() => setShowResponse(true), 500);
  };

  return (
    <section className="py-24 bg-[#1a1a1a] relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#5865F2] rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            See Yokki in Action
          </h2>
          <p className="text-xl text-[#b9bbbe] max-w-2xl mx-auto">
            Try out commands and see real-time responses
          </p>
        </motion.div>

        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-8">
          {/* Command selector */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            <div className="flex items-center gap-2 mb-6">
              <Terminal className="h-6 w-6 text-[#5865F2]" />
              <h3 className="text-2xl font-bold text-white">Try a Command</h3>
            </div>

            {demoCommands.map((cmd, index) => (
              <motion.div
                key={cmd.command}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Button
                  variant={selectedCommand.command === cmd.command ? 'default' : 'outline'}
                  className={`w-full justify-start text-left h-auto py-4 px-6 ${
                    selectedCommand.command === cmd.command
                      ? 'bg-[#5865F2] hover:bg-[#4752C4] text-white border-[#5865F2]'
                      : 'bg-[#252525] hover:bg-[#2a2a2a] text-white border-[#b9bbbe]/20'
                  }`}
                  onClick={() => handleCommandClick(cmd)}
                >
                  <div className="flex-1">
                    <div className="font-mono text-sm mb-1">{cmd.command}</div>
                    <div className="text-xs text-[#b9bbbe]">{cmd.description}</div>
                  </div>
                  <Send className="h-4 w-4 ml-2 flex-shrink-0" />
                </Button>
              </motion.div>
            ))}
          </motion.div>

          {/* Response display */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-2 mb-6">
              <div className="h-6 w-6 rounded-full bg-[#3ba55d]" />
              <h3 className="text-2xl font-bold text-white">Bot Response</h3>
            </div>

            <Card className="bg-[#252525] border-[#b9bbbe]/10 p-6 min-h-[400px]">
              {/* Discord-style message */}
              <div className="space-y-4">
                {/* User message */}
                <div className="flex items-start gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-white font-semibold">You</span>
                      <span className="text-xs text-[#b9bbbe]">Today at {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <div className="text-[#b9bbbe] font-mono text-sm">
                      {selectedCommand.command}
                    </div>
                  </div>
                </div>

                {/* Bot response */}
                <AnimatePresence mode="wait">
                  {showResponse && (
                    <motion.div
                      key={selectedCommand.command}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3 }}
                      className="flex items-start gap-3"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-semibold">Yokki</span>
                          <span className="px-1.5 py-0.5 text-[10px] bg-[#5865F2] text-white rounded font-semibold">
                            BOT
                          </span>
                          <span className="text-xs text-[#b9bbbe]">
                            Today at {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <Card className="bg-[#2b2d31] border-l-4 border-l-[#5865F2] border-t-0 border-r-0 border-b-0 p-4">
                          <div className="text-[#b9bbbe] whitespace-pre-wrap text-sm leading-relaxed">
                            {selectedCommand.response}
                          </div>
                        </Card>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {!showResponse && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center gap-2 text-[#b9bbbe]"
                  >
                    <div className="flex gap-1">
                      <motion.div
                        animate={{ opacity: [0.4, 1, 0.4] }}
                        transition={{ duration: 1.5, repeat: Infinity, delay: 0 }}
                        className="h-2 w-2 rounded-full bg-[#b9bbbe]"
                      />
                      <motion.div
                        animate={{ opacity: [0.4, 1, 0.4] }}
                        transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
                        className="h-2 w-2 rounded-full bg-[#b9bbbe]"
                      />
                      <motion.div
                        animate={{ opacity: [0.4, 1, 0.4] }}
                        transition={{ duration: 1.5, repeat: Infinity, delay: 0.4 }}
                        className="h-2 w-2 rounded-full bg-[#b9bbbe]"
                      />
                    </div>
                    <span className="text-sm">Yokki is typing...</span>
                  </motion.div>
                )}
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
